import dayjs, { type Dayjs } from 'dayjs';
import { motion, s } from '@/theme/tokens';
import type { DateQuickFilter } from '@/features/shared/hooks/useDateWindow';

export const buildToolbarIconButtonSx = (
  toolbarInactiveIconColor: string,
  toolbarControlSize: number,
) =>
  ({
    color: toolbarInactiveIconColor,
    p: '0 !important',
    width: toolbarControlSize,
    height: toolbarControlSize,
    minWidth: toolbarControlSize,
    position: 'relative',
    borderRadius: '50%',
    backgroundColor: 'transparent',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 0,
    '&:hover, &.Mui-focusVisible': {
      backgroundColor: 'transparent',
    },
    '& .MuiSvgIcon-root': {
      display: 'block',
    },
  }) as const;

export const buildToolbarIconCircleSx = (isHovered: boolean): Record<string, string | number> => ({
  width: 30,
  height: 30,
  minWidth: 30,
  minHeight: 30,
  maxWidth: 30,
  maxHeight: 30,
  flex: '0 0 30px',
  aspectRatio: '1 / 1',
  borderRadius: '50%',
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: `background-color ${motion.durations.fast} ${motion.easing.standard}`,
  backgroundColor: isHovered ? 'action.hover' : 'transparent',
});

export const tableDatePickerOpenButtonSx = (hasValue: boolean) =>
  ({
    transform: `translateX(${s('none')})`,
    '& .MuiSvgIcon-root': {
      mr: s('none'),
      color: hasValue ? 'text.primary' : 'text.secondary',
    },
  }) as const;

export const tableDatePickerTextFieldSx = {
  mt: `-${s('xs')}`,
  width: `calc(${s('4xl')} + ${s('xl')} + ${s('sm')})`,
  '& .MuiPickersInputBase-root': {
    height: 32,
    minHeight: 32,
    backgroundColor: 'background.default',
    paddingLeft: `${s('sm')} !important`,
    paddingRight: `${s('xs')} !important`,
  },
  '& .MuiPickersOutlinedInput-root, & .MuiPickersInputBase-sectionsContainer, & .MuiPickersSectionList-root, & .MuiPickersInputBase-input':
    { paddingLeft: `${s('sm')} !important` },
  '& .MuiPickersOutlinedInput-notchedOutline': {
    borderWidth: '1px',
  },
  '&:hover .MuiPickersOutlinedInput-notchedOutline': {
    borderColor: 'primary.main',
    borderWidth: '1px',
  },
  '& .MuiPickersInputBase-root.Mui-focused .MuiPickersOutlinedInput-notchedOutline': {
    borderColor: 'primary.main',
    borderWidth: '1px',
  },
  '& .MuiInputAdornment-root': { marginRight: `${s('xs')} !important` },
} as const;

export const tableDateQuickRangeTextSx = {
  '& .MuiPickersInputBase-input, & .MuiPickersInputBase-sectionContent, & .MuiPickersInputBase-separator, & .MuiPickersInputBase-sectionBefore, & .MuiPickersInputBase-sectionAfter':
    {
      color: 'text.secondary',
    },
  '& .MuiPickersSectionList-sectionSeparator': {
    color: 'text.secondary',
  },
} as const;

export const resolveDatePreview = (
  dateQuickFilter: DateQuickFilter,
  fromDate: Dayjs | null,
  toDate: Dayjs | null,
  previewFromDate: Dayjs | null = null,
  previewToDate: Dayjs | null = null,
): {
  isQuickRangePreview: boolean;
  displayedFromDate: Dayjs | null;
  displayedToDate: Dayjs | null;
} => {
  if (previewFromDate !== null || previewToDate !== null) {
    return {
      isQuickRangePreview: false,
      displayedFromDate: previewFromDate,
      displayedToDate: previewToDate,
    };
  }
  if (dateQuickFilter === 'latest' && fromDate === null && toDate === null) {
    return {
      isQuickRangePreview: false,
      displayedFromDate: null,
      displayedToDate: null,
    };
  }
  const quickFilterDays = dateQuickFilter === '14d' ? 14 : dateQuickFilter === '30d' ? 30 : 7;
  const isQuickRangePreview = fromDate === null && toDate === null;
  if (!isQuickRangePreview) {
    return {
      isQuickRangePreview,
      displayedFromDate: fromDate,
      displayedToDate: toDate,
    };
  }
  const quickRangeEnd = dayjs().startOf('day');
  const quickRangeStart = quickRangeEnd.subtract(quickFilterDays - 1, 'day');
  return {
    isQuickRangePreview,
    displayedFromDate: quickRangeStart,
    displayedToDate: quickRangeEnd,
  };
};

type ToolbarSummaryLabelArgs = {
  count: number | null;
  singularLabel?: string;
  pluralLabel?: string;
  dateQuickFilter: DateQuickFilter;
  displayedFromDate: Dayjs | null;
  displayedToDate: Dayjs | null;
};

export type TableQueryMeta<T> = { total: number; items: T[] };

export const resolveMetaDateBounds = <T>(
  meta: TableQueryMeta<T> | null,
  getDate: (item: T) => Date | string | null | undefined,
): { from: Dayjs | null; to: Dayjs | null } => {
  if (meta === null || meta.items.length === 0) {
    return { from: null, to: null };
  }
  const timestamps = meta.items
    .map((item) => getDate(item))
    .flatMap((value) => {
      if (value === null || value === undefined) return [];
      const resolved = dayjs(value);
      return resolved.isValid() ? [resolved] : [];
    });
  if (timestamps.length === 0) {
    return { from: null, to: null };
  }
  return {
    from: timestamps.reduce((earliest, current) =>
      current.isBefore(earliest) ? current : earliest,
    ),
    to: timestamps.reduce((latest, current) => (current.isAfter(latest) ? current : latest)),
  };
};

export const buildToolbarSummaryLabel = ({
  count: _count,
  singularLabel: _singularLabel,
  pluralLabel: _pluralLabel,
  dateQuickFilter: _dateQuickFilter,
  displayedFromDate,
  displayedToDate,
}: ToolbarSummaryLabelArgs): string | null => {
  if (displayedFromDate && displayedToDate) {
    return `${displayedFromDate.format('MMM D, YYYY')} - ${displayedToDate.format('MMM D, YYYY')}`;
  }
  if (displayedFromDate) {
    return `From ${displayedFromDate.format('MMM D, YYYY')}`;
  }
  if (displayedToDate) {
    return `To ${displayedToDate.format('MMM D, YYYY')}`;
  }
  return null;
};
